import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "react-toastify";
import RoleForm from "../../components/modals/RoleFormModal";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // 🔹 Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setRoles(rolesRes.data);
      setFilteredRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (err) {
      toast.error("Error al cargar roles ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // 🔍 Filtro
  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const filtered = roles.filter((r) => r.name.toLowerCase().includes(lower));
    setFilteredRoles(filtered);
    setCurrentPage(1);
  }, [searchTerm, roles]);

  // 🔹 Paginación
  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRoles = filteredRoles.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleCreate = () => {
    setSelectedRole(null);
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    if (role.sistema) {
      toast.warn("Este rol es del sistema y no se puede eliminar");
      return;
    }

    if (!window.confirm(`¿Eliminar el rol "${role.name}"?`)) return;

    try {
      await axios.delete(`${API_URL}/roles/${role._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info("Rol eliminado 🗑️");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar rol");
    }
  };

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Roles y permisos</h2>

          <div className="flex gap-2 items-center w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar rol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {hasPermission("roles.crear") && (
              <Button onClick={handleCreate}>+ Nuevo Rol</Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        <div className="flex items-center justify-end mb-2">
          <PageSizeSelect
            value={itemsPerPage}
            onChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
              <tr>
                <th className="py-2 px-4 text-left">Nombre</th>
                <th className="py-2 px-4 text-left">Tipo</th>
                <th className="py-2 px-4 text-left">Permisos</th>
                <th className="py-2 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentRoles.map((r) => (
                <tr key={r._id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4 font-medium">{r.name}</td>
                  <td className="py-2 px-4">
                    {r.sistema ? (
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                        Sistema
                      </span>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        Empresa
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-4">{r.permissions.length}</td>
                  <td className="py-2 px-4 text-right">
                    {hasPermission("roles.editar") && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={r.sistema}
                        onClick={() => handleEdit(r)}
                      >
                        Editar
                      </Button>
                    )}
                    {hasPermission("roles.eliminar") && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="ml-2 bg-red-500 hover:bg-red-600 text-white"
                        disabled={r.sistema}
                        onClick={() => handleDelete(r)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}

              {currentRoles.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-4 text-gray-500 italic"
                  >
                    No se encontraron roles.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🔹 Paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
            <span>
              Mostrando {currentRoles.length} de {filteredRoles.length}{" "}
              resultados
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                ← Anterior
              </Button>
              <span className="px-2 py-1 bg-gray-100 rounded-md">
                Página {currentPage} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}

        {showModal && (
          <RoleForm
            role={selectedRole}
            permissions={permissions}
            onClose={() => setShowModal(false)}
            onSaved={fetchData}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default RolesPage;
